import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

export const IPV4_VALIDATOR_ERROR_KEY = 'ipv4';
export const IPV4_DEFAULT_MAX_MASK_LENGTH = 3;

const IPV4_OCTET = '(25[0-5]|2[0-4]\\d|1\\d\\d|[1-9]?\\d)';
const IPV4_ADDRESS = `${IPV4_OCTET}(\\.${IPV4_OCTET}){3}`;

export interface Ipv4WithMaskValidatorOptions {
  /** Max number of digits of the mask part (after `/`). Defaults to 3. */
  maxMaskLength?: number;
}

/**
 * Builds the regex matching an IPv4 address followed by a mandatory `/` mask of 1..maxMaskLength digits.
 */
export const buildIpv4WithMaskRegExp = (maxMaskLength: number = IPV4_DEFAULT_MAX_MASK_LENGTH): RegExp =>
  new RegExp(`^${IPV4_ADDRESS}\\/\\d{1,${Math.max(1, maxMaskLength)}}$`);

/** Regex matching a bare IPv4 address (no mask). */
export const IPV4_ADDRESS_REGEXP = new RegExp(`^${IPV4_ADDRESS}$`);

/**
 * Validates an IPv4 address with a mandatory mask (e.g. `192.168.1.130/26`).
 * Text-mask placeholders (`_`) are ignored, so guided values like `10.0.0.1/8__` are accepted.
 * Empty values are considered valid: combine with `Validators.required` when needed.
 * A value made only of the displayed mask (`___.___.___.___/___`) returns `{ required: true }`.
 * Error: `{ ipv4: true }`.
 */
export const ipv4WithMaskValidator = (options?: Ipv4WithMaskValidatorOptions): ValidatorFn => {
  const regExp = buildIpv4WithMaskRegExp(options?.maxMaskLength ?? IPV4_DEFAULT_MAX_MASK_LENGTH);
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control?.value;
    if (value === null || value === undefined || (typeof value === 'string' && value.trim() === '')) {
      return null;
    }
    if (typeof value !== 'string') {
      return { [IPV4_VALIDATOR_ERROR_KEY]: true };
    }
    const cleaned = value.replace(/_/g, '').trim();
    if (/^[./]*$/.test(cleaned)) {
      return { required: true };
    }
    return regExp.test(cleaned) ? null : { [IPV4_VALIDATOR_ERROR_KEY]: true };
  };
};

